import { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import ScrollToTop from "./components/ScrollToTop";
import { ThemeProvider } from "./contexts/ThemeContext";
import ScrollToTopButton from "./components/feature/ScrollToTopButton";
import LoadingScreen from "./components/feature/LoadingScreen";
import OfflineBanner from "./components/feature/OfflineBanner";
import ErrorBoundary from "./components/feature/ErrorBoundary";
import { prefetchPublicData, hasWarmCache } from "./utils/api";

const MIN_SPLASH_FIRST_VISIT = 2400; // ms the wordmark is shown on a cold visit
const MIN_SPLASH_WARM = 1300; // ms on a repeat visit with cached data
const SLOW_THRESHOLD = 8000; // ms after which we warn about a poor connection
const MAX_SPLASH = 20000; // hard cap — never hold the site hostage

// One boundary around the whole route tree (not keyed by pathname): a crash
// mid-transition must stay catchable, and re-keying on every navigation can
// unmount the tree while a lazy route is still loading.
// PageTransition (per-route fade) provides the nav animation instead of an
// AnimatePresence "wait" wrapper, which can stall a lazily-loading page at
// opacity 0 on slow connections — a blank screen with no console output.
function AnimatedRoutes() {
  return (
    <ErrorBoundary>
      <AppRoutes />
    </ErrorBoundary>
  );
}

function App() {
  const [booting, setBooting] = useState(true);
  const [slow, setSlow] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const started = Date.now();
    const warm = hasWarmCache();
    const minShown = warm ? MIN_SPLASH_WARM : MIN_SPLASH_FIRST_VISIT;

    const slowTimer = setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, SLOW_THRESHOLD);

    // Hard cap: even if a request hangs forever, never hold the site hostage.
    const capTimer = setTimeout(() => {
      if (!cancelled) setBooting(false);
    }, MAX_SPLASH);

    if (warm) {
      // Repeat visit with cached data: reveal after the short minimum and
      // let the refresh happen quietly in the background — no network waits.
      prefetchPublicData();
      setTimeout(() => {
        if (!cancelled) setBooting(false);
      }, minShown);
    } else {
      // First visit: hold the wordmark until the core data has been fetched.
      const boot = async () => {
        await Promise.allSettled([prefetchPublicData()]);
        const elapsed = Date.now() - started;
        // Hold the wordmark at least minShown, but never past MAX_SPLASH.
        const hold = Math.max(0, Math.min(minShown - elapsed, MAX_SPLASH - elapsed));
        setTimeout(() => {
          if (!cancelled) setBooting(false);
        }, hold);
      };
      boot();
    }

    return () => {
      cancelled = true;
      clearTimeout(slowTimer);
      clearTimeout(capTimer);
    };
  }, [attempt]);

  const handleRetry = () => {
    setSlow(false);
    setAttempt((a) => a + 1);
  };

  return (
    <ThemeProvider>
      <I18nextProvider i18n={i18n}>
        <BrowserRouter basename={__BASE_PATH__}>
          <ScrollToTop />
          <AnimatedRoutes />
          <OfflineBanner />
          <ScrollToTopButton />
          <AnimatePresence>
            {booting && (
              <LoadingScreen key="splash" slow={slow} onRetry={handleRetry} />
            )}
          </AnimatePresence>
        </BrowserRouter>
      </I18nextProvider>
    </ThemeProvider>
  );
}

export default App;